// voiceGuide — Miko explains the game out loud, screen by screen.
//
// The FIRST time the player opens each screen, Miko speaks a short explainer
// ("This is your store — slide the price…"). Heard screens are remembered in
// localStorage so the guidance never nags — it teaches once, at the exact
// moment the screen is new. Respects the voice toggle via useVoice().speak.
//
// Two-register rule holds: Miko's delivery is hype, but every line CLEARLY
// explains what the screen does and what to do on it.

import { useEffect, useRef } from 'react';
import { useVoice } from './VoiceContext.js';

/** One explainer per DailyHub view. Short, clear, spoken once ever. */
const SCREEN_LINES: Record<string, string> = {
  hub:
    'Welcome to your studio! Tap your store to run your business. ' +
    'Set your price, drop it, and stack that cash. ' +
    'Your goal: buy money machines that pay you while you sleep — until you are free.',
  play:
    'This is your store. Slide the price and watch what happens — ' +
    'cheap sells more, pricey sells less. Find the sweet spot, then sell!',
  raise:
    'Time to get funding! Save it slow, borrow it fast, or pitch an investor. ' +
    'Each one costs you something different — pick your path.',
  life:
    'This is your freedom journey. Money machines pay you while you sleep. ' +
    'When they cover all your costs — you are free!',
  vision:
    'Your dream wall! Pin what you are building toward. ' +
    'Hit your numbers and watch the dreams light up.',
  mastermind:
    'Your crew! Recruit mentors here — they will coach you exactly when you need it.',
  services:
    'The A.I. agency! Sell your time for money. ' +
    'But watch out — your hours run out. Time has a ceiling; machines do not.',
  social:
    'The clip farm! Post clips, grow your fans. ' +
    'A big audience becomes a money machine that pays you brand deals.',
  gaming:
    'The beat lab! Practice your skill first, then compete. ' +
    'Skill wins here — not luck. Get good, then get paid.',
  trade:
    'Skin trading! Buy low, sell high. ' +
    'Look for underpriced gear, hold it, and flip it when the market is hot. Patience pays.',
  property:
    'Property play! Buy a place, renovate to force the value up, ' +
    'then flip it when the market is hot — or rent it out and get paid every week.',
  broker:
    'The deal desk! Someone needs a thing, someone has it — connect them and take a cut. ' +
    'No money needed. Your reputation closes the deals.',
  reflect:
    'Real talk time. One quick note about today — it makes you sharper tomorrow.',
  ritual:
    'Lock in! One breath, one intention. It sharpens your focus and boosts your sales all week.',
};

const LS_KEY = 'bizkid-voice-heard';

function heardSet(): Set<string> {
  try {
    return new Set(JSON.parse(localStorage.getItem(LS_KEY) ?? '[]') as string[]);
  } catch {
    return new Set();
  }
}

/** Has the player already heard the explainer for this screen? (Also used by
 *  HQ to skip its own greeting when the fuller hub welcome is about to play.) */
export function hasHeardGuide(view: string): boolean {
  return heardSet().has(view);
}

function markHeard(view: string): void {
  try {
    const s = heardSet();
    s.add(view);
    localStorage.setItem(LS_KEY, JSON.stringify([...s]));
  } catch {
    /* private mode etc. — worst case Miko explains again next session */
  }
}

/** Mount once (in DailyHub). Watches the active view; on the first-ever visit
 *  to a screen, speaks its explainer. Marks it heard immediately so a re-render
 *  or quick back-and-forth never double-plays. */
export function VoiceCoach({ view }: { view: string }) {
  const { speak, enabled } = useVoice();
  const speakingFor = useRef<string | null>(null);

  useEffect(() => {
    if (!enabled) return;
    const line = SCREEN_LINES[view];
    if (!line || hasHeardGuide(view) || speakingFor.current === view) return;
    speakingFor.current = view;
    markHeard(view);
    void speak(line);
  }, [view, enabled, speak]);

  return null;
}
